/**
 * The single timestamp formatter. Every user-facing date/time in the product
 * renders through this wrapper over `Intl.DateTimeFormat`, in the user's
 * timezone and locale — never a scattered `toLocaleString()`. Pure and
 * testable across zones.
 */
import { resolveTimeZone } from '@4f/shared';
import { InvalidDateError } from '../errors/index.ts';

/** Presets mapped to `Intl` date+time styles. */
export type DateTimeStyle = 'short' | 'medium' | 'long' | 'full';

/** What part(s) of the instant to render. */
export type DateTimePart = 'datetime' | 'date' | 'time';

export interface FormatDateTimeOptions {
  /** IANA timezone, e.g. `America/New_York`. The user's tz, always explicit. */
  readonly timeZone: string;
  /** BCP-47 locale, e.g. `en` or `ja`. Defaults to `en`. */
  readonly locale?: string;
  /** Verbosity preset. Defaults to `medium`. */
  readonly style?: DateTimeStyle;
  /** Render the date, the time, or both. Defaults to `datetime`. */
  readonly part?: DateTimePart;
}

/** A value the formatter accepts: a `Date`, epoch ms, or ISO string. */
export type DateInput = Date | number | string;

/**
 * Format `input` in the given `timeZone` and `locale`. The same instant renders
 * differently across zones — this is the one place that guarantee lives.
 *
 * A `timeZone` `Intl` cannot resolve renders in UTC rather than throwing. This is
 * the LAST line of defence, and it has to hold: `timeZone` traces back to
 * `users.timezone`, every timestamp in the product renders through here, and the
 * SPA has no error boundary — so one unresolvable stored zone would crash the
 * shell on render, taking the settings surface that could clear it down with it.
 * `PATCH /me` now rejects such a value, so the user could not even overwrite it.
 *
 * @throws {InvalidDateError} when `input` cannot be parsed to a valid date.
 */
export function formatDateTime(input: DateInput, options: FormatDateTimeOptions): string {
  const { timeZone, locale = 'en', style = 'medium', part = 'datetime' } = options;
  const date = toDate(input);

  const formatter = new Intl.DateTimeFormat(locale, {
    timeZone: resolveTimeZone(timeZone),
    ...(part !== 'time' ? { dateStyle: style } : {}),
    ...(part !== 'date' ? { timeStyle: style } : {}),
  });
  return formatter.format(date);
}

/** Coerce a {@link DateInput} to a valid `Date` or throw. */
function toDate(input: DateInput): Date {
  const date = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(date.getTime())) {
    throw new InvalidDateError(input);
  }
  return date;
}
